import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { useDependencies } from "./useDependencies";
import { useDocumentationArea } from "./useDocumentationArea";
import { DocumentationAreaNotFoundError } from "src/contexts/documentationArea/domain/errors/DocumentationAreaNotFoundError";
import { ActorId } from "src/contexts/documentationArea/domain/ActorId";

type CreateDocumentationAreaActorInvitationInput = {
  email: string;
  actorId: ActorId;
};

export const useCreateDocumentationAreaActorInvitation = () => {
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const { documentationArea } = useDocumentationArea();
  const { documentationAreaController } = useDependencies();

  if (!documentationArea) {
    throw new DocumentationAreaNotFoundError();
  }

  const { mutate, isPending, isSuccess, error } = useMutation({
    mutationFn: async (input: CreateDocumentationAreaActorInvitationInput) => {
      return await documentationAreaController.createActorInvitation(
        token,
        documentationArea.getId(),
        input,
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["DocumentationArea", documentationArea.getId()] });
      queryClient.invalidateQueries({
        queryKey: ["DocumentationAreaActorBasicData", documentationArea.getId(), variables.actorId],
      });
    },
  });

  return {
    createActorInvitation: mutate,
    isPending,
    isSuccess,
    error,
  };
};
